import type { Project, ProjectChange } from "@/interfaces/project";
import type { ProjectReview } from "@/interfaces/project_review";
import type { PublicUser } from "@/interfaces/user";
import { usePage, Link } from "@inertiajs/react";
import formatTime from "@/utils/formatTime";
import ReviewForm from "./ReviewForm";
import { humanize } from "@/utils/humanize";
import Panel from "@/components/ui/Panel";
import Badge from "@/components/ui/Badge";
import Avatar from "@/components/ui/Avatar";

const VERBS = {
  approval: "approved",
  rejection: "sent this back",
  comment: "commented",
} as const;

function When({ value }: { value: string }) {
  return (
    <span className="text-caption text-ink-soft">
      {new Date(value).toLocaleString()}
    </span>
  );
}

export default function ProjectReviews({
  project,
  ships,
  attributableEvents = [],
}: {
  attributableEvents?: { id: number; name: string }[];
  project: Project & {
    user: PublicUser;
    reviews: (ProjectReview & { author: PublicUser })[];
  };
  ships: ProjectChange[];
}) {
  const { props } = usePage();
  const isStaff =
    props.user.is_reviewer || props.user.is_fulfiller || props.user.is_admin;

  const timeline = [
    ...project.reviews.map((review) => ({ review })),
    // A held community approval isn't a review yet, but reviewers/HQ see it inline
    // so it can be authorized or discarded.
    ...(project.pending_approval
      ? [{ review: project.pending_approval, pending: true as const }]
      : []),
    ...ships.map((ship) => ({ ship })),
  ];
  timeline.sort((a, b) => {
    const aDate = new Date(
      "review" in a ? a.review.created_at : a.ship.date,
    ).valueOf();
    const bDate = new Date(
      "review" in b ? b.review.created_at : b.ship.date,
    ).valueOf();

    return aDate - bDate;
  });

  return (
    <Panel title="reviews.log">
      {project.aasm_state === "pending" ? (
        <p className="text-body text-ink-muted">
          Ship your project so we can review it.
        </p>
      ) : (
        <div className="flex flex-col gap-6">
          {project.reviews.length === 0 && !project.pending_approval ? (
            <p className="text-body text-ink-muted">
              Nobody has reviewed this yet - give us a little time.
            </p>
          ) : (
            <ol className="flex flex-col gap-5">
              {timeline.map((item, index) => {
                if ("ship" in item) {
                  const ship = item.ship;
                  const changes = Object.entries(ship.diff);

                  return (
                    <li className="flex gap-3" key={`ship-${ship.id}`}>
                      <Avatar
                        src={project.user.avatar}
                        name={project.user.username}
                        size="sm"
                      />
                      <div className="flex min-w-0 flex-col gap-1">
                        <p className="text-body text-ink">
                          <span className="font-bold">
                            {project.user.username}
                          </span>{" "}
                          <span className="italic">shipped</span>
                        </p>
                        <When value={ship.date} />
                        {changes.length > 0 && (
                          <dl className="text-caption text-ink-muted mt-1 flex flex-col gap-1 wrap-break-word">
                            {changes.map(([field, [before, after]]) => (
                              <div key={field}>
                                <dt className="text-ink font-bold">
                                  {humanize(field)}
                                </dt>
                                <dd>
                                  {before ? `"${before}"` : "empty"} → "{after}"
                                </dd>
                              </div>
                            ))}
                          </dl>
                        )}
                      </div>
                    </li>
                  );
                }

                const review = item.review;
                const isPending = "pending" in item && item.pending;
                const editHref = isPending
                  ? `/projects/${project.id}/pending_approvals/${review.id}/edit`
                  : `/projects/${project.id}/reviews/${review.id}/edit`;

                return (
                  <li
                    className="flex gap-3"
                    key={`${isPending ? "pending-" : ""}review-${review.id}`}
                  >
                    <Avatar
                      src={review.author.avatar}
                      name={review.author.username}
                      size="sm"
                    />
                    <div className="flex min-w-0 flex-col gap-1">
                      <p className="text-body text-ink flex flex-wrap items-center gap-2">
                        <span>
                          <span className="font-bold">
                            {review.author.username}
                          </span>{" "}
                          <span className="italic">
                            {VERBS[review.review_type as keyof typeof VERBS] ??
                              "commented"}
                            {review.review_type === "approval" &&
                              ` for ${formatTime(review.approved_seconds)}`}
                          </span>
                        </span>
                        {isPending && (
                          <Badge tone="warning">Pending HQ authorization</Badge>
                        )}
                      </p>
                      <When value={review.created_at} />

                      {review.content && (
                        <p className="text-body text-ink-muted mt-1 wrap-break-word whitespace-pre-wrap">
                          {review.content}
                        </p>
                      )}

                      {review.admin_content && (
                        <div className="border-carrot-orange bg-surface-alt mt-1 border-2 border-dashed p-3">
                          <p className="text-caption text-ink-muted wrap-break-word whitespace-pre-wrap">
                            {review.admin_content}
                          </p>
                        </div>
                      )}

                      {isStaff && (
                        <div className="text-caption mt-1 flex flex-wrap gap-4">
                          <Link href={editHref} className="text-link underline">
                            Edit
                          </Link>
                          {props.user.is_admin && isPending && (
                            <>
                              <Link
                                href={`/projects/${project.id}/pending_approvals/${review.id}/publish`}
                                className="text-leaf cursor-pointer font-bold underline"
                                method="post"
                                as="button"
                              >
                                Authorize
                              </Link>
                              <Link
                                href={`/projects/${project.id}/pending_approvals/${review.id}/discard`}
                                className="text-sunset-orange cursor-pointer underline"
                                method="post"
                                as="button"
                              >
                                Discard
                              </Link>
                            </>
                          )}
                          {!isPending &&
                            (index === timeline.length - 1 ||
                              review.review_type === "comment") && (
                              <Link
                                href={`/projects/${project.id}/reviews/${review.id}`}
                                className="text-sunset-orange cursor-pointer underline"
                                method="delete"
                              >
                                {review.review_type === "comment"
                                  ? "Delete"
                                  : "Undo"}
                              </Link>
                            )}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}

          {isStaff &&
            (project.pending_hq ? (
              <div className="border-carrot-orange bg-surface-alt border-2 border-dashed p-3">
                <p className="text-caption text-ink-muted">
                  {props.user.is_admin
                    ? "This project has an approval awaiting HQ authorization. Authorize or discard it above before placing a new review."
                    : "This project has an approval awaiting HQ authorization. An HQ reviewer needs to act on it before it can be reviewed further."}
                </p>
              </div>
            ) : (
              <ReviewForm
                project={project}
                attributableEvents={attributableEvents}
              />
            ))}
        </div>
      )}
    </Panel>
  );
}
